import { Container, Divider, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { groupBy, mapValues, pipe, sortBy } from 'remeda';
import { DynamicIcon, NamedLayoutBlock } from 'src/components';
import { usePartnerGroupRules } from 'src/graphql';
import { useGetNotificationEvents } from 'src/graphql/entities/notification-preference';
import { useGlobalStore } from 'src/store';
import { PushNotificationSettings } from './components/push-notification-settings';
import { NotificationsConfigsTable } from './notifications-configs-table';
import { StyledRoundedBox } from './styled';
import { getCategoryConfig, shouldShowCategory } from './utils';

export const NotificationsView = () => {
  const { notificationEvents } = useGetNotificationEvents();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { partnerGroupRules } = usePartnerGroupRules(selectedPartnerId);
  const eventsByCategory = pipe(
    notificationEvents,
    groupBy((event) => event.category),
    mapValues((events) => sortBy(events, (event) => event.name)),
  );

  const categories = Object.keys(eventsByCategory).toSorted((a, b) => a.localeCompare(b));

  const [activeCategory, setActiveCategory] = useState<string>(categories[0]);

  const handleTabChange = (_event: SyntheticEvent, value: string) => {
    setActiveCategory(value);
  };

  useEffect(() => {
    if (!activeCategory && categories.length) {
      setActiveCategory(categories[0]);
    }
  }, [activeCategory, categories]);

  if (!activeCategory) {
    return null;
  }

  return (
    <NamedLayoutBlock
      title="Notification Settings"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Container fixed maxWidth="md">
        <Tabs
          aria-label="Tab navigation"
          value={activeCategory}
          onChange={handleTabChange}
          variant="standard"
          TabIndicatorProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.custom.OV.sys.primary,
            },
          }}
        >
          {categories.map((category) => {
            const categoryConfig = getCategoryConfig(category);
            const shouldShow = shouldShowCategory(categoryConfig, partnerGroupRules);

            if (!shouldShow) {
              return null;
            }

            return (
              <Tab
                key={category}
                icon={<DynamicIcon name={categoryConfig.icon} />}
                iconPosition="start"
                label={categoryConfig.displayName}
                value={category}
                sx={{
                  minHeight: 'auto',
                  color: (theme) => theme.palette.custom.OV.sys.onSurface,
                  '&.Mui-selected': {
                    color: (theme) => theme.palette.custom.OV.sys.primary,
                  },
                }}
              />
            );
          })}
        </Tabs>
        <Divider flexItem sx={{ mt: 1, mb: 3 }} />
        <StyledRoundedBox spacing={2}>
          <Typography variant="subtitle1" fontSize={18} fontWeight={500}>
            Notification Preferences
          </Typography>
          <Typography>
            Customize how and when you receive notifications. Select the events you want to be
            notified about, specify the conditions when notifications should be sent, and
            choose your preferred notification channels.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack direction="row" gap={3} alignItems="center">
                <DynamicIcon
                  name="notifications"
                  sx={{
                    fontSize: '32px',
                  }}
                />
                <Stack>
                  <Typography variant="body1">In-app Notifications</Typography>
                  <Typography variant="caption" fontSize={12}>
                    Notifications appear in your notification center
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" gap={3} alignItems="center">
                <DynamicIcon
                  name="email"
                  sx={{
                    fontSize: '32px',
                  }}
                />
                <Stack>
                  <Typography variant="body1">Email Notifications</Typography>
                  <Typography variant="caption" fontSize={12}>
                    Notifications are sent to your email address
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={12}>
              <PushNotificationSettings />
            </Grid>
          </Grid>
        </StyledRoundedBox>
        <NotificationsConfigsTable events={eventsByCategory[activeCategory] ?? []} />
      </Container>
    </NamedLayoutBlock>
  );
};
